import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ProgramListResponse } from "../program.types";
import { USER_PROGRAMS_API, PROGRAMS_PAGE_SIZE } from "../program.constants";

/**
 * Arguments for useFetchUserPrograms hook.
 */
export type UseFetchUserProgramsArgs = {
  /** Whether the query is enabled */
  isEnabled?: boolean;
  /** Whether the store has been hydrated from localStorage */
  isHydrated?: boolean;
  /** Refetch on window focus */
  refetchOnWindowFocus?: boolean;
  /** Refetch on reconnect */
  refetchOnReconnect?: boolean;
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
  /** Current page for pagination */
  page?: number;
};

/**
 * Hook to fetch authenticated user's enrolled programs from the API.
 * Returns React Query result directly - this is the source of truth for user program data.
 *
 * @example
 * ```typescript
 * // In a component
 * const { data, isLoading } = useFetchUserPrograms({
 *   user,
 *   page: 1,
 * });
 *
 * if (isLoading) return <Loader />;
 *
 * return (
 *   <ProgramList
 *     programs={data?.programs ?? []}
 *     hasMore={data?.has_more ?? false}
 *   />
 * );
 * ```
 */
export function useFetchUserPrograms({
  isEnabled = true,
  isHydrated = true,
  refetchOnWindowFocus = true,
  refetchOnReconnect = true,
  user,
  page = 1,
}: UseFetchUserProgramsArgs) {
  // Build query string
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("page_size", String(PROGRAMS_PAGE_SIZE));

  return useQuery<ProgramListResponse, Error>({
    queryKey: ["userPrograms", page],
    queryFn: async () => {
      return apiRequest(
        `${USER_PROGRAMS_API}?${params.toString()}`,
        "GET"
      ) as Promise<ProgramListResponse>;
    },
    // Only enable the query when store is hydrated and user is logged in
    enabled: !!user && isHydrated && isEnabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus,
    refetchOnReconnect,
  });
}

export default useFetchUserPrograms;
